import { createSlice } from "@reduxjs/toolkit";
import { WeeklyLearningState } from './types';
import {
    getAllWeeklyLearningSubmissions,
    getMyWeeklyLearningSubmissions,
    getCurrentWeekSubmission,
    getWeeklyLearningSubmissionById,
    createWeeklyLearningSubmission,
    updateWeeklyLearningSubmission,
    deleteWeeklyLearningSubmission
} from './thunk';

const initialState: WeeklyLearningState = {
    submissions: [],
    currentSubmission: null,
    currentWeekSubmission: null,
    loading: false,
    error: null
};

const weeklyLearningSlice = createSlice({
    name: "weeklyLearning",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentSubmission: (state, action) => {
            state.currentSubmission = action.payload;
        },
        clearCurrentSubmission: (state) => {
            state.currentSubmission = null;
        }
    },
    extraReducers: (builder) => {
        // Get All Submissions
        builder
            .addCase(getAllWeeklyLearningSubmissions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllWeeklyLearningSubmissions.fulfilled, (state, action) => {
                state.loading = false;
                state.submissions = action.payload;
            })
            .addCase(getAllWeeklyLearningSubmissions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch submissions";
            })
            // Get My Submissions
            .addCase(getMyWeeklyLearningSubmissions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getMyWeeklyLearningSubmissions.fulfilled, (state, action) => {
                state.loading = false;
                state.submissions = action.payload;
            })
            .addCase(getMyWeeklyLearningSubmissions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch submissions";
            })
            // Get Current Week Submission
            .addCase(getCurrentWeekSubmission.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCurrentWeekSubmission.fulfilled, (state, action) => {
                state.loading = false;
                state.currentWeekSubmission = action.payload;
            })
            .addCase(getCurrentWeekSubmission.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch current week submission";
            })
            // Get Submission By ID
            .addCase(getWeeklyLearningSubmissionById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getWeeklyLearningSubmissionById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSubmission = action.payload;
            })
            .addCase(getWeeklyLearningSubmissionById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch submission";
            })
            // Create Submission
            .addCase(createWeeklyLearningSubmission.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createWeeklyLearningSubmission.fulfilled, (state, action) => {
                state.loading = false;
                // Check if submission already exists (shouldn't, but safety check)
                const existingIndex = state.submissions.findIndex(sub => sub.id === action.payload.id);
                if (existingIndex === -1) {
                    state.submissions.unshift(action.payload);
                } else {
                    // Update if it exists
                    state.submissions[existingIndex] = action.payload;
                }
                if (action.payload.week_start_date === state.currentWeekSubmission?.week_start_date) {
                    state.currentWeekSubmission = action.payload;
                }
            })
            .addCase(createWeeklyLearningSubmission.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to create submission";
            })
            // Update Submission
            .addCase(updateWeeklyLearningSubmission.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateWeeklyLearningSubmission.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.submissions.findIndex(sub => sub.id === action.payload.id);
                if (index !== -1) {
                    state.submissions[index] = action.payload;
                } else {
                    // If not found in submissions list, add it (might have been created elsewhere)
                    state.submissions.unshift(action.payload);
                }
                if (state.currentSubmission?.id === action.payload.id) {
                    state.currentSubmission = action.payload;
                }
                if (state.currentWeekSubmission?.id === action.payload.id) {
                    state.currentWeekSubmission = action.payload;
                }
            })
            .addCase(updateWeeklyLearningSubmission.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to update submission";
            })
            // Delete Submission
            .addCase(deleteWeeklyLearningSubmission.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteWeeklyLearningSubmission.fulfilled, (state, action) => {
                state.loading = false;
                state.submissions = state.submissions.filter(sub => sub.id !== action.payload);
                if (state.currentSubmission?.id === action.payload) {
                    state.currentSubmission = null;
                }
                if (state.currentWeekSubmission?.id === action.payload) {
                    state.currentWeekSubmission = null;
                }
            })
            .addCase(deleteWeeklyLearningSubmission.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to delete submission";
            });
    }
});

export const { clearError, setCurrentSubmission, clearCurrentSubmission } = weeklyLearningSlice.actions;
export default weeklyLearningSlice.reducer;

